import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Button, Input, Space, Modal, Form, 
  message, Tag, Typography, InputNumber, Tabs, 
  Descriptions, Progress, Row, Col, Statistic
} from 'antd';
import {
  UserAddOutlined,
  DollarOutlined,
  EditOutlined,
  HistoryOutlined,
  PercentageOutlined,
  BankOutlined,
  EyeOutlined,
  DeleteOutlined,
  StopOutlined,
  PlayCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionWrapper from '../../components/PermissionWrapper';
import agentService from '../../services/agent.service';

const { Title } = Typography;

const Agents = () => {
  const { user } = useAuth();
  const { canCreateAgent, canWrite, isObserver } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [observers, setObservers] = useState([]);
  const [creditRecords, setCreditRecords] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [observerPagination, setObserverPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createObserverModalVisible, setCreateObserverModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [recordsModalVisible, setRecordsModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('agents');

  // 觀察者操作確認對話框狀態
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [statusConfirmVisible, setStatusConfirmVisible] = useState(false);
  const [pendingObserver, setPendingObserver] = useState(null);
  const [observerActionLoading, setObserverActionLoading] = useState(false);

  // 代理解鎖相關狀態
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [selectedUnlockAgent, setSelectedUnlockAgent] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);

  // Stage 2: 代理停用/刪除相關狀態
  const [removeConfirmVisible, setRemoveConfirmVisible] = useState(false);
  const [agentActionLoading, setAgentActionLoading] = useState(false);
  const [pendingActionAgent, setPendingActionAgent] = useState(null);

  // Stage 3: 級聯刪除相關狀態
  const [cascadeDeleteVisible, setCascadeDeleteVisible] = useState(false);
  const [cascadeDeleteData, setCascadeDeleteData] = useState(null);
  const [cascadeDeleteLoading, setCascadeDeleteLoading] = useState(false);

  // Stage 3: 停用/啟用選項對話框
  const [suspendOptionsVisible, setSuspendOptionsVisible] = useState(false);
  const [activateOptionsVisible, setActivateOptionsVisible] = useState(false);
  const [treeActionLoading, setTreeActionLoading] = useState(false);

  const [form] = Form.useForm();
  const [observerForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [unlockForm] = Form.useForm();
  const [agentActionForm] = Form.useForm(); // Stage 2: 代理操作原因表單
  const [cascadeDeleteForm] = Form.useForm(); // Stage 3: 級聯刪除表單

  useEffect(() => {
    loadDashboard();
    loadAgents();
    if (!isObserver()) {
      loadObservers();
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [pagination.current]);

  useEffect(() => {
    if (!isObserver()) {
      loadObservers();
    }
  }, [observerPagination.current]);

  const loadDashboard = async () => {
    try {
      const response = await agentService.getDashboard();
      setDashboardData(response);
    } catch (error) {
      console.error('載入儀表板失敗:', error);
    }
  };

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await agentService.getSubAgents({
        page: pagination.current,
        limit: pagination.pageSize,
        status: 'all'
      });
      
      if (response && response.data) {
        setAgents(response.data);
        setPagination({
          ...pagination,
          total: response.pagination?.total || 0
        });
      } else if (response && Array.isArray(response.agents)) {
        setAgents(response.agents);
        setPagination({
          ...pagination,
          total: response.pagination?.total || 0
        });
      } else {
        console.error('Invalid agents response:', response);
        setAgents([]);
      }
    } catch (error) {
      console.error('Agents API Error:', error);
      message.error('載入代理列表失敗');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadObservers = async () => {
    // 觀察者不需要載入觀察者列表
    if (isObserver()) {
      console.log('當前用戶是觀察者，跳過載入觀察者列表');
      return;
    }

    try {
      console.log('開始載入觀察者列表...');
      setLoading(true);
      const response = await agentService.getObservers({
        page: observerPagination.current,
        limit: observerPagination.pageSize
      });

      console.log('觀察者列表回應:', response);

      if (response && response.data) {
        console.log('設置觀察者列表:', response.data.length, '個觀察者');
        setObservers(response.data);
        setObserverPagination({
          ...observerPagination,
          total: response.pagination?.total || 0
        });
      } else {
        console.error('Invalid observers response:', response);
        setObservers([]);
      }
    } catch (error) {
      console.error('Observers API Error:', error);
      // 只有非觀察者才顯示錯誤信息
      if (!isObserver()) {
        message.error('載入觀察者列表失敗');
      }
      setObservers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCreditRecords = async () => {
    try {
      setLoading(true);
      const response = await agentService.getCreditRecords({
        page: 1,
        limit: 50
      });

      // 修復：確保正確處理響應資料結構
      if (response && response.records) {
        setCreditRecords(response.records);
      } else if (response && response.data) {
        setCreditRecords(response.data);
      } else if (response && Array.isArray(response)) {
        setCreditRecords(response);
      } else {
        console.error('Invalid credit records response:', response);
        setCreditRecords([]);
        message.warning('沒有找到額度記錄');
      }
    } catch (error) {
      console.error('載入額度記錄失敗:', error);
      message.error('載入額度記錄失敗');
      setCreditRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (values) => {
    try {
      const response = await agentService.createAgent(values);
      if (response.success) {
        message.success('代理建立成功');
        setCreateModalVisible(false);
        form.resetFields();
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      // 獲取錯誤訊息
      let alertMessage = '❌ 建立代理失敗\n\n';

      if (error.response?.data?.error) {
        alertMessage += error.response.data.error;

        // 如果有詳細資訊，加入詳細信息
        if (error.response.data.details) {
          const details = error.response.data.details;

          alertMessage += '\n\n';
          if (details.username) {
            alertMessage += `帳號：${details.username}\n`;
          }
          if (details.availableCredit !== undefined) {
            alertMessage += `可用額度：${details.availableCredit}\n`;
          }
          if (details.requestedCredit !== undefined) {
            alertMessage += `請求額度：${details.requestedCredit}\n`;
          }
          if (details.instruction) {
            alertMessage += `\n處理方式：${details.instruction}`;
          }
        }
      } else if (error.message) {
        alertMessage += error.message;
      } else {
        alertMessage += '發生未知錯誤';
      }

      window.alert(alertMessage);
    }
  };

  const handleCreateObserver = async (values) => {
    try {
      const response = await agentService.createObserver(values);
      if (response.success) {
        message.success('觀察者帳號建立成功');
        setCreateObserverModalVisible(false);
        observerForm.resetFields();
        loadObservers();
      }
    } catch (error) {
      // 獲取錯誤訊息
      let alertMessage = '❌ 建立觀察者失敗\n\n';

      if (error.response?.data?.error) {
        alertMessage += error.response.data.error;

        // 如果有詳細資訊，加入詳細信息
        if (error.response.data.details) {
          const details = error.response.data.details;

          alertMessage += '\n\n';
          if (details.username) {
            alertMessage += `帳號：${details.username}\n`;
          }
          if (details.instruction) {
            alertMessage += `\n處理方式：${details.instruction}`;
          }
        }
      } else if (error.message) {
        alertMessage += error.message;
      } else {
        alertMessage += '發生未知錯誤';
      }

      window.alert(alertMessage);
    }
  };

  // 解鎖代理帳號
  const handleUnlockAgent = async (values) => {
    try {
      setUnlockLoading(true);
      const response = await agentService.unlockAgent(
        selectedUnlockAgent.id,
        values.reason || ''
      );

      if (response.success) {
        // 判斷是代理還是觀察者
        const targetType = selectedUnlockAgent.role === 'observer' ? '觀察者' : '代理';
        message.success(`已成功解鎖${targetType} ${selectedUnlockAgent.displayName}`);
        setUnlockModalVisible(false);
        unlockForm.resetFields();
        setSelectedUnlockAgent(null);
        // 重新載入列表（代理或觀察者）
        loadAgents();
        loadObservers();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || '解鎖失敗';
      message.error(errorMessage);
    } finally {
      setUnlockLoading(false);
    }
  };

  // 開始刪除觀察者確認
  const handleDeleteObserver = (observerId, observerName) => {
    setPendingObserver({ id: observerId, name: observerName, action: 'delete' });
    setDeleteConfirmVisible(true);
  };

  // 執行刪除觀察者
  const executeDeleteObserver = async () => {
    if (!pendingObserver || observerActionLoading) return;

    try {
      setObserverActionLoading(true);
      console.log('開始刪除觀察者:', pendingObserver.id, pendingObserver.name);
      const response = await agentService.deleteObserver(pendingObserver.id);
      console.log('刪除觀察者回應:', response);

      // 由於 api.js 自動解包 response.data，response 就是後端返回的對象
      if (response && response.success) {
        message.success(response.message || '觀察者帳號已刪除');
        await loadObservers(); // 重新載入觀察者列表
      } else {
        console.error('刪除觀察者失敗:', response);
        message.error(response?.error || '刪除失敗');
      }
    } catch (error) {
      console.error('刪除觀察者錯誤:', error);
      const errorMessage = error.response?.data?.error || error.message || '刪除失敗';
      message.error(errorMessage);
    } finally {
      setObserverActionLoading(false);
      setDeleteConfirmVisible(false);
      setPendingObserver(null);
    }
  };

  // 開始狀態切換確認
  const handleToggleObserverStatus = (observerId, currentStatus, observerName) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? '啟用' : '停用';

    setPendingObserver({
      id: observerId,
      name: observerName,
      currentStatus,
      newStatus,
      action: 'toggle'
    });
    setStatusConfirmVisible(true);
  };

  // 執行狀態切換
  const executeToggleObserverStatus = async () => {
    if (!pendingObserver || observerActionLoading) return;

    try {
      setObserverActionLoading(true);
      const { id, newStatus } = pendingObserver;
      console.log(`開始切換觀察者狀態:`, id, pendingObserver.name, `${pendingObserver.currentStatus} -> ${newStatus}`);

      const response = await agentService.updateObserverStatus(id, newStatus);
      console.log(`觀察者狀態切換回應:`, response);

      // 由於 api.js 自動解包 response.data，response 就是後端返回的對象
      if (response && response.success) {
        const actionText = newStatus === 'active' ? '啟用' : '停用';
        message.success(response.message || `觀察者帳號已${actionText}`);
        await loadObservers(); // 重新載入觀察者列表
      } else {
        console.error('狀態切換失敗:', response);
        message.error(response?.error || '狀態切換失敗');
      }
    } catch (error) {
      console.error(`觀察者狀態切換錯誤:`, error);
      const errorMessage = error.response?.data?.error || error.message || '狀態切換失敗';
      message.error(errorMessage);
    } finally {
      setObserverActionLoading(false);
      setStatusConfirmVisible(false);
      setPendingObserver(null);
    }
  };

  const handleUpdateSettings = async (values) => {
    try {
      const response = await agentService.updateAgent(selectedAgent.id, values);
      if (response.success) {
        message.success('設定更新成功');
        setSettingsModalVisible(false);
        settingsForm.resetFields();
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      message.error(error.response?.data?.error || '更新失敗');
    }
  };


  // Stage 2: 刪除代理處理函數
  const handleRemoveAgent = async (agent) => {
    try {
      // 先獲取最新的刪除狀態
      const response = await agentService.checkAgentDeletable(agent.id);
      if (response.success) {
        // 使用後端回傳的最新資料（包含 isDeletable 和 checks）
        setPendingActionAgent({
          ...agent,
          ...response.agent,
          isDeletable: response.isDeletable,  // 使用後端判斷的結果
          checks: response.checks,             // 包含詳細的檢查結果
          _count: {
            children: response.agent.subAgentCount
          },
          totalMembers: response.agent.memberCount
        });
        setRemoveConfirmVisible(true);
      }
    } catch (error) {
      console.error('檢查代理可刪除狀態失敗:', error);
      message.error('無法獲取代理狀態，請稍後再試');
    }
  };

  const executeRemoveAgent = async () => {
    const reason = agentActionForm.getFieldValue('reason');
    setAgentActionLoading(true);

    try {
      const response = await agentService.removeAgent(pendingActionAgent.id, reason);
      if (response.success) {
        message.success('代理已刪除');
        setRemoveConfirmVisible(false);
        setSettingsModalVisible(false);
        agentActionForm.resetFields();
        setPendingActionAgent(null);
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      console.log('刪除代理錯誤:', error);
      console.log('錯誤回應:', error.response);

      // 顯示詳細錯誤訊息
      const errorData = error.response?.data;

      if (errorData?.validationErrors && Array.isArray(errorData.validationErrors) && errorData.validationErrors.length > 0) {
        // 前置檢查失敗，使用原生 alert 確保顯示
        let alertMessage = `❌ 無法刪除代理：${errorData.error || '前置檢查失敗'}\n\n`;
        alertMessage += '未滿足的條件：\n';
        errorData.validationErrors.forEach((err, idx) => {
          alertMessage += `\n${idx + 1}. ${err.message}\n`;
          if (err.detail) {
            alertMessage += `   詳情：${err.detail}\n`;
          }
          if (err.instruction) {
            alertMessage += `   處理方式：${err.instruction}\n`;
          }
        });

        window.alert(alertMessage);
      } else if (errorData?.error) {
        // 顯示一般錯誤訊息
        window.alert(`❌ 刪除失敗\n\n${errorData.error}`);
      } else {
        // 預設錯誤訊息
        window.alert(`❌ 刪除失敗\n\n${error.message || '發生未知錯誤，請稍後再試'}`);
      }
    } finally {
      setAgentActionLoading(false);
    }
  };

  // ============================================================
  // Stage 3: 級聯刪除相關函數
  // ============================================================

  const handleCascadeDelete = async (agent) => {
    try {
      setLoading(true);
      console.log('🚀 前端：開始獲取級聯刪除預覽', { agentId: agent.id });

      // 1. 獲取預覽數據
      const preview = await agentService.getCascadeDeletePreview(agent.id);

      console.log('✅ 前端：收到預覽數據', preview);

      if (!preview.success) {
        console.error('❌ 前端：預覽失敗', preview);
        message.error('獲取預覽失敗');
        return;
      }

      // 2. 檢查驗算結果
      console.log('🔍 前端：檢查 canDelete', { canDelete: preview.canDelete });

      if (!preview.canDelete) {
        console.error('❌ 前端：canDelete 為 false，顯示錯誤對話框');
        Modal.error({
          title: '❌ 無法刪除：帳目不符',
          width: 600,
          content: (
            <div>
              <p><strong>驗算結果：</strong></p>
              <ul>
                <li>期望值（L1 總額度）：${preview.verification.expected?.toLocaleString()}</li>
                <li>計算值（會員餘額+代理剩餘）：${preview.verification.calculated?.toLocaleString()}</li>
                <li>差異：${preview.verification.difference?.toLocaleString()}</li>
              </ul>
              <p style={{ color: 'red', fontWeight: 'bold' }}>帳目存在異常，請聯繫技術人員檢查數據</p>
            </div>
          )
        });
        return;
      }

      // 3. 顯示預覽確認對話框（使用受控 Modal）
      console.log('✅ 前端：canDelete 為 true，準備顯示確認對話框');
      setCascadeDeleteData({ agent, preview });
      setCascadeDeleteVisible(true);

    } catch (error) {
      console.error('❌ 前端：級聯刪除錯誤', {
        error,
        message: error.message,
        response: error.response,
        code: error.code
      });

      // 檢查是否為超時錯誤
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        Modal.error({
          title: '請求超時',
          content: '操作超時，請檢查網路連接或聯繫技術支援'
        });
      } else if (error.response?.data?.error === 'INVALID_PASSWORD') {
        message.error('密碼錯誤');
      } else {
        Modal.error({
          title: '刪除失敗',
          content: error.response?.data?.error || error.message || '未知錯誤'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Stage 3: 停用/啟用整條線相關函數
  // ============================================================

  // 顯示停用選項對話框
  const handleSuspendAgent = (agent) => {
    setPendingActionAgent(agent);
    setSuspendOptionsVisible(true);
  };

  // 執行停用單一代理
  const executeSuspendSingle = async () => {
    const reason = agentActionForm.getFieldValue('reason');
    setAgentActionLoading(true);

    try {
      const response = await agentService.suspendAgent(pendingActionAgent.id, reason);
      if (response.success) {
        message.success('代理已停用');
        setSuspendOptionsVisible(false);
        setSettingsModalVisible(false);
        agentActionForm.resetFields();
        setPendingActionAgent(null);
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      console.error('停用代理錯誤:', error);
      window.alert(`❌ 停用失敗\n\n${error.response?.data?.error || '發生未知錯誤，請稍後再試'}`);
    } finally {
      setAgentActionLoading(false);
    }
  };

  // 執行停用整條線
  const executeSuspendTree = async () => {
    const reason = agentActionForm.getFieldValue('reason');
    setTreeActionLoading(true);

    try {
      const response = await agentService.suspendAgentTree(pendingActionAgent.id, reason);
      if (response.success) {
        message.success(`已停用整條線：${response.statistics.totalAgents} 個代理，${response.statistics.totalMembers} 個會員`);
        setSuspendOptionsVisible(false);
        setSettingsModalVisible(false);
        agentActionForm.resetFields();
        setPendingActionAgent(null);
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      console.error('停用整條線錯誤:', error);
      window.alert(`❌ 停用整條線失敗\n\n${error.response?.data?.error || '發生未知錯誤，請稍後再試'}`);
    } finally {
      setTreeActionLoading(false);
    }
  };

  // 顯示啟用選項對話框
  const handleActivateAgent = (agent) => {
    setPendingActionAgent(agent);
    setActivateOptionsVisible(true);
  };

  // 執行啟用單一代理
  const executeActivateSingle = async () => {
    const reason = agentActionForm.getFieldValue('reason');
    setAgentActionLoading(true);

    try {
      const response = await agentService.activateAgent(pendingActionAgent.id, reason);
      if (response.success) {
        message.success('代理已啟用');
        setActivateOptionsVisible(false);
        setSettingsModalVisible(false);
        agentActionForm.resetFields();
        setPendingActionAgent(null);
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      console.error('啟用代理錯誤:', error);
      window.alert(`❌ 啟用失敗\n\n${error.response?.data?.error || '發生未知錯誤，請稍後再試'}`);
    } finally {
      setAgentActionLoading(false);
    }
  };

  // 執行啟用整條線
  const executeActivateTree = async () => {
    const reason = agentActionForm.getFieldValue('reason');
    setTreeActionLoading(true);

    try {
      const response = await agentService.activateAgentTree(pendingActionAgent.id, reason);
      if (response.success) {
        message.success(`已啟用整條線：${response.statistics.totalAgents} 個代理，${response.statistics.totalMembers} 個會員`);
        setActivateOptionsVisible(false);
        setSettingsModalVisible(false);
        agentActionForm.resetFields();
        setPendingActionAgent(null);
        loadAgents();
        loadDashboard();
      }
    } catch (error) {
      console.error('啟用整條線錯誤:', error);
      window.alert(`❌ 啟用整條線失敗\n\n${error.response?.data?.error || '發生未知錯誤，請稍後再試'}`);
    } finally {
      setTreeActionLoading(false);
    }
  };

  const getLevelName = (level) => {
    const levelNames = {
      0: '管理員',
      1: '一級代理',
      2: '二級代理',
      3: '三級代理',
      4: '四級代理',
      5: '五級代理'
    };

    // 如果是預定義的層級，直接返回
    if (levelNames[level]) {
      return levelNames[level];
    }

    // Level 6 以上，轉換為中文數字
    const chineseNumbers = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

    if (level >= 6 && level <= 10) {
      return `${chineseNumbers[level]}級代理`;
    } else if (level > 10 && level < 20) {
      const ones = level % 10;
      return `十${chineseNumbers[ones]}級代理`;
    } else if (level >= 20) {
      const tens = Math.floor(level / 10);
      const ones = level % 10;
      if (ones === 0) {
        return `${chineseNumbers[tens]}十級代理`;
      } else {
        return `${chineseNumbers[tens]}十${chineseNumbers[ones]}級代理`;
      }
    }

    return `${level}級代理`;
  };

  // 貨幣格式化函數 - 使用簡單的方式避免轉義問題
  const formatNumber = (value) => {
    if (!value) return '$ 0';
    return '$ ' + value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  const parseNumber = (value) => {
    if (!value) return value;
    return value.replace(/\$\s?|,/g, '');
  };

  const columns = [
    {
      title: '帳號',
      dataIndex: 'username',
      key: 'username',
      width: window.innerWidth <= 768 ? 100 : 120,
    },
    {
      title: '顯示名稱',
      dataIndex: 'displayName',
      key: 'displayName',
      width: window.innerWidth <= 768 ? 100 : 120,
    },
    {
      title: '層級',
      dataIndex: 'level',
      key: 'level',
      width: window.innerWidth <= 768 ? 60 : 80,
      render: (level) => getLevelName(level),
    },
    {
      title: '額度管理',
      key: 'credit',
      width: window.innerWidth <= 768 ? 150 : 200,
      render: (_, record) => {
        const usage = record.creditLimit > 0 ? (record.usedCredit / record.creditLimit) * 100 : 0;
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>總額:</span>
              <span>${record.creditLimit.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>已用:</span>
              <span>${record.usedCredit.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#52c41a' }}>可用:</span>
              <span style={{ color: '#52c41a' }}>
                ${record.availableCredit.toLocaleString()}
              </span>
            </div>
            <Progress 
              percent={usage} 
              size="small" 
              status={usage > 90 ? 'exception' : usage > 70 ? 'active' : 'success'}
              format={() => `${usage.toFixed(1)}%`}
            />
          </Space>
        );
      },
    },
    {
      title: '佔成設定',
      key: 'commission',
      width: window.innerWidth <= 768 ? 100 : 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>佔成: {record.commissionRate || 0}%</span>
          <span>洗碼佣金: {record.rebateRate || 0}%</span>
        </Space>
      ),
    },
    {
      title: '下級統計',
      key: 'stats',
      width: window.innerWidth <= 768 ? 80 : 100,
      render: (_, record) => {
        // 確保數據存在，如果沒有則顯示默認值
        const totalMembers = record.totalMembers || 0;
        const hasSubAgents = record.hasSubAgents || false;
        
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: window.innerWidth <= 768 ? '10px' : '12px' }}>
              👥 {totalMembers}
            </span>
            <span style={{ fontSize: window.innerWidth <= 768 ? '10px' : '12px' }}>
              {hasSubAgents ? '🏢 有下級' : '🔹 無下級'}
            </span>
          </Space>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: window.innerWidth <= 768 ? 80 : 120,
      render: (status, record) => {
        // 優先顯示登入鎖定狀態
        if (record.loginLocked) {
          return (
            <Space direction="vertical" size={0}>
              <Tag color="orange" icon={<LockOutlined />}>
                登入鎖定
              </Tag>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {record.lastLogin ? new Date(record.lastLogin).toLocaleDateString() : '未登入'}
              </span>
            </Space>
          );
        }

        // Stage 2: 增強狀態顯示 - 區分 active, inactive, deleted
        let statusColor = 'green';
        let statusText = '正常';

        if (status === 'inactive') {
          statusColor = 'orange';
          statusText = '已停用';
        } else if (status === 'deleted') {
          statusColor = 'red';
          statusText = '已刪除';
        } else if (status === 'active') {
          statusColor = 'green';
          statusText = '正常';
        }

        return (
          <Space direction="vertical" size={0}>
            <Tag color={statusColor}>
              {statusText}
            </Tag>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {record.lastLogin ? new Date(record.lastLogin).toLocaleDateString() : '未登入'}
            </span>
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: window.innerWidth <= 768 ? false : 'right',
      width: window.innerWidth <= 768 ? 100 : 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedAgent(record);
              setSettingsModalVisible(true);
              // 修復：確保佔成率有預設值，避免 null/undefined
              settingsForm.setFieldsValue({
                creditLimit: record.creditLimit || 0,
                commissionRate: record.commissionRate || 0,
                rebateRate: record.rebateRate || 0
              });
            }}
          >
            {isObserver() ? '查看詳情' : '設定管理'}
          </Button>
          {user?.level === 0 && record.status !== 'deleted' && (
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleCascadeDelete(record)}
            >
              級聯刪除
            </Button>
          )}
          {record.loginLocked && !isObserver() && (
            <Button
              type="link"
              size="small"
              icon={<UnlockOutlined />}
              style={{ color: '#ff9c6e' }}
              onClick={() => {
                setSelectedUnlockAgent(record);
                setUnlockModalVisible(true);
                unlockForm.resetFields();
              }}
            >
              解鎖登入
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const observerColumns = [
    {
      title: '帳號',
      dataIndex: 'username',
      key: 'username',
      width: window.innerWidth <= 768 ? 120 : 180,
    },
    {
      title: '顯示名稱',
      dataIndex: 'displayName',
      key: 'displayName',
      width: window.innerWidth <= 768 ? 100 : 150,
    },
    {
      title: '層級',
      dataIndex: 'level',
      key: 'level',
      width: window.innerWidth <= 768 ? 80 : 100,
      render: (level) => getLevelName(level),
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: window.innerWidth <= 768 ? 100 : 150,
      render: (date) => new Date(date).toLocaleDateString('zh-TW'),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: window.innerWidth <= 768 ? 60 : 80,
      render: (status, record) => {
        // 優先檢查是否被鎖定
        if (record.loginLocked) {
          return <Tag color="orange">登入鎖定</Tag>;
        }
        // 再檢查一般狀態
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {status === 'active' ? '正常' : '停用'}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: window.innerWidth <= 768 ? false : 'right',
      width: window.innerWidth <= 768 ? 120 : 150,
      render: (_, record) => (
        <div>
          {/* 如果被鎖定，顯示解鎖按鈕 */}
          {record.loginLocked ? (
            <Button
              type="primary"
              size="small"
              icon={<UnlockOutlined />}
              style={{ marginRight: 8, marginBottom: 4 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUnlockAgent(record);
                setUnlockModalVisible(true);
              }}
            >
              解鎖
            </Button>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={record.status === 'active' ? <StopOutlined /> : <PlayCircleOutlined />}
              style={{ marginRight: 8, marginBottom: 4 }}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleObserverStatus(record.id, record.status, record.displayName);
              }}
            >
              {record.status === 'active' ? '停用' : '啟用'}
            </Button>
          )}
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteObserver(record.id, record.displayName);
            }}
          >
            刪除
          </Button>
        </div>
      ),
    },
  ];

  const creditColumns = [
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
    },
    {
      title: '操作類型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeMap = {
          'allocate': { text: '分配額度', color: 'blue' },
          'reclaim': { text: '回收額度', color: 'orange' },
          'deposit_to_member': { text: '會員充值', color: 'green' },
          'withdraw_from_member': { text: '會員提領', color: 'red' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const isPositive = ['allocate', 'deposit_to_member'].includes(record.type);
        return (
          <span style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
            {isPositive ? '+' : '-'}${Math.abs(amount).toLocaleString()}
          </span>
        );
      },
    },
    {
      title: '相關對象',
      key: 'related',
      render: (_, record) => {
        if (record.related) {
          return record.related.displayName || record.related.username;
        }
        return '-';
      },
    },
    {
      title: '說明',
      dataIndex: 'note',
      key: 'note',
    },
  ];

  // 計算剩餘可分配額度
  const getAvailableCredit = () => {
    if (!dashboardData?.creditInfo) return 0;
    return dashboardData.creditInfo.availableCredit;
  };

  // 修復：下級代理的最大佔成率應該是當前用戶的佔成率
  const getMaxCommissionRate = () => {
    return dashboardData?.commissionInfo?.commissionRate || 0;
  };

  // 修復：下級代理的最大洗碼佣金率應該是當前用戶的洗碼佣金率
  const getMaxRebateRate = () => {
    return user?.rebateRate || dashboardData?.commissionInfo?.rebateRate || 0;
  };

  return (
    <div>
      <Title level={2}>代理管理中心</Title>
      
      {/* 概覽卡片 */}
      {dashboardData && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="我的額度"
                value={dashboardData.creditInfo.creditLimit}
                prefix={<DollarOutlined />}
                formatter={(value) => `${value?.toLocaleString()}`}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="可用額度"
                value={dashboardData.creditInfo.availableCredit}
                prefix={<DollarOutlined />}
                formatter={(value) => `${value?.toLocaleString()}`}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="我的佔成率"
                value={dashboardData.commissionInfo.commissionRate}
                prefix={<PercentageOutlined />}
                formatter={(value) => `${value?.toFixed(2)}`}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="下級代理數"
                value={dashboardData.stats.subAgentsCount}
                prefix={<UserAddOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>
      )}
      
      <Card>
        {/* 手機版：將操作按鈕放在 Tabs 上方 */}
        {window.innerWidth <= 768 && (
          <div style={{ marginBottom: 16 }}>
            <Space wrap style={{ width: '100%', justifyContent: 'center' }}>
              <Button
                icon={<HistoryOutlined />}
                onClick={() => {
                  loadCreditRecords();
                  setRecordsModalVisible(true);
                }}
                size="small"
              >
                記錄
              </Button>
              <PermissionWrapper
                requireWrite={true}
                disabled={!canCreateAgent()}
                observerText="僅供查看"
              >
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => {
                    setCreateModalVisible(true);
                    // 設定預設值
                    form.setFieldsValue({
                      commissionRate: getMaxCommissionRate(),
                      rebateRate: getMaxRebateRate()
                    });
                  }}
                  size="small"
                >
                  新增代理
                </Button>
              </PermissionWrapper>
              {!isObserver() && (
                <PermissionWrapper
                  requireWrite={true}
                  observerText="僅供查看"
                >
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => setCreateObserverModalVisible(true)}
                    size="small"
                  >
                    新增觀察
                  </Button>
                </PermissionWrapper>
              )}
            </Space>
          </div>
        )}
        
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type={window.innerWidth <= 768 ? "line" : "card"}
          tabBarExtraContent={
            // 桌面版：操作按鈕放在 tab 右側
            window.innerWidth > 768 ? (
              <Space wrap>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => {
                    loadCreditRecords();
                    setRecordsModalVisible(true);
                  }}
                  size="middle"
                >
                  額度記錄
                </Button>
                <PermissionWrapper
                  requireWrite={true}
                  disabled={!canCreateAgent()}
                  observerText="僅供查看"
                >
                  <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={() => {
                      setCreateModalVisible(true);
                      // 設定預設值
                      form.setFieldsValue({
                        commissionRate: getMaxCommissionRate(),
                        rebateRate: getMaxRebateRate()
                      });
                    }}
                    size="middle"
                  >
                    新增代理
                  </Button>
                </PermissionWrapper>
                {!isObserver() && (
                  <PermissionWrapper
                    requireWrite={true}
                    observerText="僅供查看"
                  >
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setCreateObserverModalVisible(true)}
                      size="middle"
                    >
                      新增觀察帳號
                    </Button>
                  </PermissionWrapper>
                )}
              </Space>
            ) : null
          }
          items={[
            {
              key: 'agents',
              label: '代理列表',
              children: (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={agents}
                  loading={loading}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 筆`,
                    onChange: (page, pageSize) => {
                      setPagination({ ...pagination, current: page, pageSize });
                    }
                  }}
                  scroll={{ x: window.innerWidth <= 768 ? 800 : 1200 }}
                  size={window.innerWidth <= 768 ? "small" : "middle"}
                />
              )
            },
            // 只有非觀察者才能看到觀察者管理頁籤
            ...(!isObserver() ? [{
              key: 'observers',
              label: '觀察者帳號',
              children: (
                <Table
                  rowKey="id"
                  columns={observerColumns}
                  dataSource={observers}
                  loading={false}
                  pagination={{
                    ...observerPagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 筆觀察者帳號`,
                    onChange: (page, pageSize) => {
                      setObserverPagination({ ...observerPagination, current: page, pageSize });
                    }
                  }}
                  scroll={{ x: window.innerWidth <= 768 ? 600 : 1000 }}
                  size={window.innerWidth <= 768 ? "small" : "middle"}
                />
              )
            }] : [])
          ]}
        />
      </Card>

      {/* 新增代理 Modal */}
      <Modal
        title="新增下級代理"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form form={form} onFinish={handleCreateAgent} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="帳號"
                rules={[
                  { required: true, message: '請輸入帳號' },
                  { pattern: /^[a-zA-Z0-9]{4,20}$/, message: '帳號須為4-20位英數字' }
                ]}
              >
                <Input placeholder="請輸入帳號 (4-20位英數字)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="顯示名稱"
                rules={[{ required: true, message: '請輸入顯示名稱' }]}
              >
                <Input placeholder="請輸入顯示名稱" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="password"
            label="密碼"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼至少6位' }
            ]}
          >
            <Input.Password placeholder="請輸入密碼 (至少6位)" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="creditLimit"
                label="信用額度"
                rules={[
                  { required: true, message: '請輸入信用額度' },
                  { type: 'number', min: 0, message: '額度不能為負數' }
                ]}
                extra={`可分配額度: ${getAvailableCredit() === '無限制' ? '無限制' : `$${getAvailableCredit().toLocaleString()}`}`}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="請輸入信用額度"
                  formatter={formatNumber}
                  parser={parseNumber}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="代理層級">
                <Input value={getLevelName(user?.level + 1)} disabled />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="commissionRate"
                label="佔成率 (%)"
                rules={[
                  { required: true, message: '請輸入佔成率' },
                  { type: 'number', min: 0, max: getMaxCommissionRate(), 
                    message: `佔成率必須在 0 - ${getMaxCommissionRate()}% 之間` }
                ]}
                extra={`您的佔成率: ${getMaxCommissionRate()}%`}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={getMaxCommissionRate()}
                  precision={2}
                  step={0.1}
                  placeholder="請輸入佔成率"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rebateRate"
                label="洗碼佣金率 (%)"
                rules={[
                  { required: true, message: '請輸入洗碼佣金率' },
                  { type: 'number', min: 0, max: getMaxRebateRate(), 
                    message: `洗碼佣金率必須在 0 - ${getMaxRebateRate()}% 之間` }
                ]}
                extra={`您的洗碼佣金率: ${getMaxRebateRate()}%`}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={getMaxRebateRate()}
                  precision={3}
                  step={0.001}
                  placeholder="請輸入洗碼佣金率"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setCreateModalVisible(false)}>
                取消
              </Button>
              <PermissionWrapper
                disabled={true}
                observerText="僅供查看"
              >
                <Button type="primary" htmlType="submit">
                  確認新增
                </Button>
              </PermissionWrapper>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增觀察者 Modal */}
      <Modal
        title="新增觀察者帳號"
        open={createObserverModalVisible}
        onCancel={() => {
          setCreateObserverModalVisible(false);
          observerForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={observerForm} onFinish={handleCreateObserver} layout="vertical">
          <Form.Item
            name="username"
            label="帳號名稱"
            rules={[
              { required: true, message: '請輸入帳號名稱' },
              { pattern: /^[a-zA-Z0-9_]{3,20}$/, message: '帳號須為3-20位英數字或底線' }
            ]}
          >
            <Input placeholder="請輸入觀察者帳號 (3-20位英數字或底線)" />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="顯示名稱"
            rules={[
              { required: true, message: '請輸入顯示名稱' },
              { max: 20, message: '顯示名稱不能超過20個字符' }
            ]}
          >
            <Input placeholder="請輸入觀察者顯示名稱" />
          </Form.Item>

          <Form.Item
            name="password"
            label="登入密碼"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼至少6位' },
              { max: 20, message: '密碼不能超過20位' }
            ]}
          >
            <Input.Password placeholder="請輸入登入密碼 (6-20位)" />
          </Form.Item>

          <div style={{
            background: '#f6f6f6',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#666' }}>觀察者權限說明</h4>
            <ul style={{ margin: 0, paddingLeft: '16px', color: '#666', fontSize: '13px' }}>
              <li>✅ 可查看您的所有代理下級資料</li>
              <li>✅ 可查看會員投注、報表等信息</li>
              <li>❌ 無法進行任何修改操作</li>
              <li>❌ 無法創建代理或管理會員</li>
            </ul>
          </div>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setCreateObserverModalVisible(false)}>
                取消
              </Button>
              <PermissionWrapper
                disabled={true}
                observerText="僅供查看"
              >
                <Button type="primary" htmlType="submit">
                  確認新增
                </Button>
              </PermissionWrapper>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 代理設定管理 Modal */}
      <Modal
        title={`管理代理設定 - ${selectedAgent?.displayName}`}
        open={settingsModalVisible}
        onCancel={() => {
          setSettingsModalVisible(false);
          settingsForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedAgent && (
          <>
            <Descriptions
              bordered
              column={{ xs: 1, sm: 1, md: 2 }}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="帳號">{selectedAgent.username}</Descriptions.Item>
              <Descriptions.Item label="層級">{getLevelName(selectedAgent.level)}</Descriptions.Item>
              <Descriptions.Item label="當前佔成率">{selectedAgent.commissionRate || 0}%</Descriptions.Item>
              <Descriptions.Item label="當前洗碼佣金率">{selectedAgent.rebateRate || 0}%</Descriptions.Item>
              <Descriptions.Item label="已使用額度">
                ${selectedAgent.usedCredit.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="額度使用率">
                {selectedAgent.creditUsagePercent}%
              </Descriptions.Item>
            </Descriptions>
            
            <Form form={settingsForm} onFinish={handleUpdateSettings} layout="vertical">
              <Form.Item
                name="creditLimit"
                label="信用額度"
                rules={[
                  { required: true, message: '請輸入信用額度' },
                  { type: 'number', min: selectedAgent.usedCredit, 
                    message: `新額度不能小於已使用額度 ${selectedAgent.usedCredit.toLocaleString()}` }
                ]}
                extra={`已使用: $${selectedAgent.usedCredit.toLocaleString()}, 可分配: ${getAvailableCredit() === '無限制' ? '無限制' : `$${getAvailableCredit().toLocaleString()}`}`}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="請輸入新額度"
                  formatter={formatNumber}
                  parser={parseNumber}
                />
              </Form.Item>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="commissionRate"
                    label="佔成率 (%)"
                    rules={[
                      { required: true, message: '請輸入佔成率' },
                      { type: 'number', min: 0, max: getMaxCommissionRate(), 
                        message: `佔成率必須在 0 - ${getMaxCommissionRate()}% 之間` }
                    ]}
                    extra={`最高: ${getMaxCommissionRate()}%`}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={getMaxCommissionRate()}
                      precision={2}
                      step={0.1}
                      placeholder="請輸入佔成率"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="rebateRate"
                    label="洗碼佣金率 (%)"
                    rules={[
                      { required: true, message: '請輸入洗碼佣金率' },
                      { type: 'number', min: 0, max: getMaxRebateRate(), 
                        message: `洗碼佣金率必須在 0 - ${getMaxRebateRate()}% 之間` }
                    ]}
                    extra={`最高: ${getMaxRebateRate()}%`}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={getMaxRebateRate()}
                      precision={3}
                      step={0.001}
                      placeholder="請輸入洗碼佣金率"
                    />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="reason"
                label="調整原因"
              >
                <Input.TextArea rows={3} placeholder="請輸入調整原因（選填）" />
              </Form.Item>
              
              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  {/* 左側：停用/啟用和刪除按鈕 */}
                  <Space size="middle">
                    {selectedAgent?.status === 'active' ? (
                      <PermissionWrapper disabled={true} observerText="僅供查看">
                        <Button
                          icon={<StopOutlined />}
                          danger
                          onClick={() => handleSuspendAgent(selectedAgent)}
                        >
                          停用代理
                        </Button>
                      </PermissionWrapper>
                    ) : selectedAgent?.status === 'inactive' ? (
                      <PermissionWrapper disabled={true} observerText="僅供查看">
                        <Button
                          icon={<PlayCircleOutlined />}
                          type="primary"
                          onClick={() => handleActivateAgent(selectedAgent)}
                        >
                          啟用代理
                        </Button>
                      </PermissionWrapper>
                    ) : null}

                    <PermissionWrapper disabled={true} observerText="僅供查看">
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleRemoveAgent(selectedAgent)}
                        style={{ marginLeft: 8 }}
                      >
                        刪除代理
                      </Button>
                    </PermissionWrapper>
                  </Space>

                  {/* 右側：取消和確認更新按鈕 */}
                  <Space>
                    <Button onClick={() => setSettingsModalVisible(false)}>
                      取消
                    </Button>
                    <PermissionWrapper
                      disabled={true}
                      observerText="僅供查看"
                    >
                      <Button type="primary" htmlType="submit">
                        確認更新
                      </Button>
                    </PermissionWrapper>
                  </Space>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 額度記錄 Modal */}
      <Modal
        title="額度變更記錄"
        open={recordsModalVisible}
        onCancel={() => setRecordsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          rowKey="id"
          columns={creditColumns}
          dataSource={creditRecords}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `共 ${total} 筆記錄`
          }}
          size="small"
        />
      </Modal>

      {/* Stage 3: 停用代理選項對話框 */}
      <Modal
        title={`停用代理 - ${pendingActionAgent?.displayName}`}
        open={suspendOptionsVisible}
        onCancel={() => {
          if (!agentActionLoading && !treeActionLoading) {
            setSuspendOptionsVisible(false);
            setPendingActionAgent(null);
            agentActionForm.resetFields();
          }
        }}
        footer={null}
        width={600}
        maskClosable={!agentActionLoading && !treeActionLoading}
        closable={!agentActionLoading && !treeActionLoading}
      >
        {pendingActionAgent && (
          <div>
            <p style={{ marginBottom: 16 }}>
              請選擇停用範圍：
            </p>

            <Form form={agentActionForm} layout="vertical">
              <Form.Item name="reason" label="停用原因（選填）">
                <Input.TextArea rows={3} placeholder="請輸入停用原因" />
              </Form.Item>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                {/* 停用單一代理 */}
                <Button
                  danger
                  size="large"
                  loading={agentActionLoading}
                  disabled={treeActionLoading}
                  onClick={executeSuspendSingle}
                  style={{
                    flex: 1,
                    height: 'auto',
                    padding: '16px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <StopOutlined style={{ fontSize: 20 }} />
                    <div style={{ fontWeight: 'bold' }}>停用此代理</div>
                    <div style={{ fontSize: 12, color: '#999', fontWeight: 'normal' }}>
                      只停用 {pendingActionAgent.displayName}
                    </div>
                  </div>
                </Button>

                {/* 停用整條線 */}
                <Button
                  danger
                  size="large"
                  loading={treeActionLoading}
                  disabled={agentActionLoading}
                  onClick={executeSuspendTree}
                  style={{
                    flex: 1,
                    height: 'auto',
                    padding: '16px',
                    background: '#ff4d4f',
                    borderColor: '#ff4d4f',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: 'white' }}>
                    <StopOutlined style={{ fontSize: 20 }} />
                    <div style={{ fontWeight: 'bold' }}>停用整條線</div>
                    <div style={{ fontSize: 12, fontWeight: 'normal' }}>
                      包含所有下級代理和會員
                    </div>
                  </div>
                </Button>
              </div>

              <div style={{ marginTop: 16, padding: 12, background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#8c8c8c' }}>
                  <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                  <strong>說明：</strong>
                </p>
                <ul style={{ margin: '8px 0 0 20px', padding: 0, fontSize: 12, color: '#8c8c8c' }}>
                  <li><strong>停用此代理：</strong>只停用當前代理帳號，下級代理不受影響</li>
                  <li><strong>停用整條線：</strong>停用此代理及所有下級代理，並禁止相關會員投注</li>
                </ul>
              </div>
            </Form>
          </div>
        )}
      </Modal>

      {/* Stage 3: 啟用代理選項對話框 */}
      <Modal
        title={`啟用代理 - ${pendingActionAgent?.displayName}`}
        open={activateOptionsVisible}
        onCancel={() => {
          if (!agentActionLoading && !treeActionLoading) {
            setActivateOptionsVisible(false);
            setPendingActionAgent(null);
            agentActionForm.resetFields();
          }
        }}
        footer={null}
        width={600}
        maskClosable={!agentActionLoading && !treeActionLoading}
        closable={!agentActionLoading && !treeActionLoading}
      >
        {pendingActionAgent && (
          <div>
            <p style={{ marginBottom: 16 }}>
              請選擇啟用範圍：
            </p>

            <Form form={agentActionForm} layout="vertical">
              <Form.Item name="reason" label="啟用原因（選填）">
                <Input.TextArea rows={3} placeholder="請輸入啟用原因" />
              </Form.Item>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                {/* 啟用單一代理 */}
                <Button
                  type="primary"
                  size="large"
                  loading={agentActionLoading}
                  disabled={treeActionLoading}
                  onClick={executeActivateSingle}
                  style={{
                    flex: 1,
                    height: 'auto',
                    padding: '16px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <PlayCircleOutlined style={{ fontSize: 20 }} />
                    <div style={{ fontWeight: 'bold' }}>啟用此代理</div>
                    <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 'normal' }}>
                      只啟用 {pendingActionAgent.displayName}
                    </div>
                  </div>
                </Button>

                {/* 啟用整條線 */}
                <Button
                  type="primary"
                  size="large"
                  loading={treeActionLoading}
                  disabled={agentActionLoading}
                  onClick={executeActivateTree}
                  style={{
                    flex: 1,
                    height: 'auto',
                    padding: '16px',
                    background: '#52c41a',
                    borderColor: '#52c41a',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <PlayCircleOutlined style={{ fontSize: 20 }} />
                    <div style={{ fontWeight: 'bold' }}>啟用整條線</div>
                    <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 'normal' }}>
                      包含所有下級代理和會員
                    </div>
                  </div>
                </Button>
              </div>

              <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#8c8c8c' }}>
                  <CheckCircleOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                  <strong>說明：</strong>
                </p>
                <ul style={{ margin: '8px 0 0 20px', padding: 0, fontSize: 12, color: '#8c8c8c' }}>
                  <li><strong>啟用此代理：</strong>只啟用當前代理帳號，下級代理不受影響</li>
                  <li><strong>啟用整條線：</strong>啟用此代理及所有下級代理，並恢復相關會員投注權限</li>
                </ul>
              </div>
            </Form>
          </div>
        )}
      </Modal>

      {/* Stage 2: 刪除代理確認對話框 */}
      <Modal
        title="確認刪除代理"
        open={removeConfirmVisible}
        onOk={executeRemoveAgent}
        onCancel={() => {
          if (!agentActionLoading) {
            setRemoveConfirmVisible(false);
            setPendingActionAgent(null);
            agentActionForm.resetFields();
          }
        }}
        okText="確認刪除"
        cancelText="取消"
        okType="danger"
        okButtonProps={{
          danger: true,
          loading: agentActionLoading,
          disabled: agentActionLoading
        }}
        cancelButtonProps={{
          disabled: agentActionLoading
        }}
        maskClosable={!agentActionLoading}
        closable={!agentActionLoading}
      >
        {pendingActionAgent && (() => {
          // 計算是否可刪除
          const usedCredit = pendingActionAgent.usedCredit || 0;
          const creditLimit = pendingActionAgent.creditLimit || 0;
          const subAgentCount = pendingActionAgent.subAgentCount || pendingActionAgent._count?.children || 0;
          const memberCount = pendingActionAgent.memberCount || pendingActionAgent.totalMembers || 0;

          // 優先使用後端回傳的 isDeletable（包含額度一致性檢查）
          // 如果沒有（舊資料），則使用前端簡單判斷
          const isDeletable = pendingActionAgent.isDeletable !== undefined
            ? pendingActionAgent.isDeletable
            : (usedCredit === 0 && subAgentCount === 0 && memberCount === 0);

          // Debug: 在 console 顯示資料來源
          console.log('刪除 Modal 資料:', {
            pendingActionAgent,
            usedCredit,
            creditLimit,
            subAgentCount,
            memberCount,
            isDeletable,
            dataSource: {
              subAgentCount_from: pendingActionAgent.subAgentCount ? 'subAgentCount' : '_count.children',
              memberCount_from: pendingActionAgent.memberCount ? 'memberCount' : 'totalMembers'
            }
          });

          return (
            <div>
              <p>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                確定要刪除代理 <strong>{pendingActionAgent.displayName}</strong> ({pendingActionAgent.username}) 嗎？
              </p>

              {/* 額度狀態區塊 */}
              <div style={{
                background: isDeletable ? '#f6ffed' : '#fff1f0',
                border: `1px solid ${isDeletable ? '#b7eb8f' : '#ffa39e'}`,
                borderRadius: 4,
                padding: 12,
                marginTop: 12
              }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: isDeletable ? '#52c41a' : '#ff4d4f', marginBottom: 8 }}>
                  {isDeletable ? <CheckCircleOutlined style={{ marginRight: 4 }} /> : <CloseCircleOutlined style={{ marginRight: 4 }} />}
                  {isDeletable ? '可刪除' : '不可刪除'}
                </p>
                <div style={{ fontSize: 13, color: '#595959', lineHeight: '20px' }}>
                  <div><strong>總額度：</strong>{creditLimit.toLocaleString()}</div>
                  <div>
                    <strong>已用額度：</strong>{usedCredit.toLocaleString()}
                    <span style={{ color: '#999', marginLeft: 4 }}></span>
                  </div>
                  <div><strong>下級代理數：</strong>{subAgentCount}</div>
                  <div><strong>直屬會員數：</strong>{memberCount}</div>
                </div>
                {isDeletable && creditLimit > 0 && (
                  <div style={{
                    marginTop: 10,
                    padding: 8,
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                    fontSize: 13,
                    color: '#096dd9'
                  }}>
                    將收回 <strong>{creditLimit.toLocaleString()}</strong> 額度至你的「{user?.displayName || user?.username}」
                  </div>
                )}
                {!isDeletable && (
                  <div style={{
                    marginTop: 10,
                    padding: 8,
                    background: '#fff2e8',
                    border: '1px solid #ffbb96',
                    borderRadius: 4,
                    fontSize: 12,
                    color: '#d4380d'
                  }}>
                    請先處理：
                    {usedCredit > 0 && <div>• 回收所有已分配額度</div>}
                    {subAgentCount > 0 && <div>• 移除所有下級代理</div>}
                    {memberCount > 0 && <div>• 移除所有直屬會員</div>}
                  </div>
                )}
              </div>

              <div style={{ background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, padding: 12, marginTop: 12 }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#d4380d' }}>⚠️ 刪除前置條件：</p>
                <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0, color: '#8c8c8c' }}>
                  <li>該代理的已使用額度必須為 0</li>
                  <li>該代理不能有下線代理</li>
                  <li>該代理不能有直屬會員</li>
                </ul>
              </div>

              <Form form={agentActionForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="reason" label="刪除原因（選填）">
                  <Input.TextArea rows={3} placeholder="請輸入刪除原因" />
                </Form.Item>
              </Form>
            </div>
          );
        })()}
      </Modal>

      {/* 刪除觀察者確認對話框 */}
      <Modal
        title="確認刪除觀察者帳號"
        open={deleteConfirmVisible}
        onOk={executeDeleteObserver}
        onCancel={() => {
          if (!observerActionLoading) {
            setDeleteConfirmVisible(false);
            setPendingObserver(null);
          }
        }}
        okText="確認刪除"
        cancelText="取消"
        okType="danger"
        okButtonProps={{
          danger: true,
          loading: observerActionLoading,
          disabled: observerActionLoading
        }}
        cancelButtonProps={{
          disabled: observerActionLoading
        }}
        maskClosable={!observerActionLoading}
        closable={!observerActionLoading}
      >
        <p>確定要刪除觀察者帳號「{pendingObserver?.name}」嗎？</p>
        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>此操作無法復原！</p>
        {observerActionLoading && (
          <p style={{ color: '#1890ff', marginTop: 8 }}>正在刪除中，請稍候...</p>
        )}
      </Modal>

      {/* 觀察者狀態切換確認對話框 */}
      <Modal
        title={`確認${pendingObserver?.newStatus === 'active' ? '啟用' : '停用'}觀察者帳號`}
        open={statusConfirmVisible}
        onOk={executeToggleObserverStatus}
        onCancel={() => {
          if (!observerActionLoading) {
            setStatusConfirmVisible(false);
            setPendingObserver(null);
          }
        }}
        okText={`確認${pendingObserver?.newStatus === 'active' ? '啟用' : '停用'}`}
        cancelText="取消"
        okType={pendingObserver?.newStatus === 'active' ? 'primary' : 'danger'}
        okButtonProps={{
          loading: observerActionLoading,
          disabled: observerActionLoading
        }}
        cancelButtonProps={{
          disabled: observerActionLoading
        }}
        maskClosable={!observerActionLoading}
        closable={!observerActionLoading}
      >
        <p>
          確定要{pendingObserver?.newStatus === 'active' ? '啟用' : '停用'}觀察者帳號「{pendingObserver?.name}」嗎？
        </p>
        {observerActionLoading && (
          <p style={{ color: '#1890ff', marginTop: 8 }}>
            正在{pendingObserver?.newStatus === 'active' ? '啟用' : '停用'}中，請稍候...
          </p>
        )}
      </Modal>

      {/* 解鎖代理帳號 Modal */}
      <Modal
        title={
          <Space>
            <UnlockOutlined style={{ color: '#ff9c6e' }} />
            解鎖代理帳號
          </Space>
        }
        open={unlockModalVisible}
        onCancel={() => {
          if (!unlockLoading) {
            setUnlockModalVisible(false);
            setSelectedUnlockAgent(null);
            unlockForm.resetFields();
          }
        }}
        footer={null}
        width={600}
      >
        <Form
          form={unlockForm}
          onFinish={handleUnlockAgent}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <div style={{
            background: '#fff7e6',
            border: '1px solid #ffd666',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
              <strong>代理帳號資訊</strong>
            </div>
            <div>
              <p><strong>帳號：</strong>{selectedUnlockAgent?.username}</p>
              <p><strong>名稱：</strong>{selectedUnlockAgent?.displayName}</p>
              <p><strong>狀態：</strong>
                <Tag color="orange" icon={<LockOutlined />} style={{ marginLeft: 8 }}>
                  登入鎖定
                </Tag>
              </p>
              {selectedUnlockAgent?.loginFailCount > 0 && (
                <p><strong>失敗次數：</strong>{selectedUnlockAgent.loginFailCount} 次</p>
              )}
            </div>
          </div>

          <div style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            padding: 12,
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <UnlockOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                解鎖後該代理將能夠正常登入
              </span>
            </div>
          </div>

          <Form.Item
            name="reason"
            label="解鎖原因（選填）"
          >
            <Input.TextArea
              rows={3}
              placeholder="請輸入解鎖該代理帳號的原因（選填）..."
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setUnlockModalVisible(false);
                  setSelectedUnlockAgent(null);
                  unlockForm.resetFields();
                }}
                disabled={unlockLoading}
              >
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={unlockLoading}
                icon={<UnlockOutlined />}
                style={{ background: '#ff9c6e', borderColor: '#ff9c6e' }}
              >
                確認解鎖
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Stage 3: 級聯刪除確認對話框 */}
      <Modal
        title={`⚠️ 級聯刪除確認 - ${cascadeDeleteData?.agent?.displayName}`}
        open={cascadeDeleteVisible}
        onCancel={() => {
          if (!cascadeDeleteLoading) {
            setCascadeDeleteVisible(false);
            setCascadeDeleteData(null);
            cascadeDeleteForm.resetFields();
          }
        }}
        footer={null}
        width={800}
        maskClosable={!cascadeDeleteLoading}
        closable={!cascadeDeleteLoading}
      >
        {cascadeDeleteData && (
          <div>
            {/* 驗算結果 - 緊湊版 */}
            <div style={{ marginBottom: 12, padding: 10, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 'bold', fontSize: 13 }}>帳目驗算通過</span>
                <span style={{ fontSize: 12, color: '#52c41a' }}>
                  L1額度 ${cascadeDeleteData.preview.verification.expected?.toLocaleString()} = 會員+代理 ${cascadeDeleteData.preview.verification.calculated?.toLocaleString()}
                </span>
              </div>

              {/* 統計資訊 - 合併顯示 */}
              <Row gutter={8}>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: '8px 0', backgroundColor: '#fff', borderRadius: 3, border: '1px solid #d9d9d9' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>代理</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#262626' }}>
                      {cascadeDeleteData.preview.statistics.totalAgents}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: '8px 0', backgroundColor: '#fff', borderRadius: 3, border: '1px solid #d9d9d9' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>會員</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#262626' }}>
                      {cascadeDeleteData.preview.statistics.totalMembers}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: '8px 0', backgroundColor: '#fff', borderRadius: 3, border: '1px solid #d9d9d9' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>觀察者</div>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#262626' }}>
                      {cascadeDeleteData.preview.statistics.totalObservers}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center', padding: '8px 0', backgroundColor: '#fff', borderRadius: 3, border: '1px solid #d9d9d9' }}>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>回收額度</div>
                    <div style={{ fontSize: 13, fontWeight: 'bold', color: '#262626' }}>
                      ${cascadeDeleteData.preview.statistics.totalCreditToRecover?.toLocaleString()}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* 會員餘額警告 - 緊湊版 */}
              {cascadeDeleteData.preview.statistics.totalMemberBalance > 0 && (
                <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 3, fontSize: 12 }}>
                  <span style={{ fontWeight: 'bold', color: '#d46b08' }}>警告：</span>
                  <span style={{ color: '#d46b08' }}>
                    會員餘額 ${cascadeDeleteData.preview.statistics.totalMemberBalance?.toLocaleString()} 將被清空且無法恢復
                  </span>
                </div>
              )}

              {/* 詳細明細 - 可展開查看 */}
              <details style={{ marginTop: 8, fontSize: 12 }}>
                <summary style={{ cursor: 'pointer', color: '#595959', fontWeight: 'bold' }}>
                  查看詳細明細
                </summary>
                <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', padding: 8, backgroundColor: '#fff', borderRadius: 3 }}>
                  {/* 代理明細 */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#262626' }}>
                      代理 ({(cascadeDeleteData.preview.breakdown.agents?.length || 0) + 1})
                    </div>
                    <div style={{ fontSize: 11 }}>
                      <div style={{ padding: '2px 4px', backgroundColor: '#e6f7ff', marginBottom: 2 }}>
                        <strong>{cascadeDeleteData.preview.breakdown.targetAgent.displayName}</strong>
                        <span style={{ float: 'right', color: '#52c41a' }}>
                          ${cascadeDeleteData.preview.breakdown.targetAgent.availableCredit?.toLocaleString()}
                        </span>
                      </div>
                      {cascadeDeleteData.preview.breakdown.agents?.map((agent, idx) => (
                        <div key={idx} style={{ padding: '2px 4px', backgroundColor: '#f5f5f5', marginBottom: 2 }}>
                          {agent.displayName}
                          <span style={{ float: 'right', color: '#52c41a' }}>
                            ${agent.availableCredit?.toLocaleString()}
                          </span>
                        </div>
                      ))}
                      <div style={{ padding: '2px 4px', backgroundColor: '#d9d9d9', fontWeight: 'bold' }}>
                        合計
                        <span style={{ float: 'right' }}>
                          ${cascadeDeleteData.preview.statistics.totalAgentAvailableCredit?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 會員明細 */}
                  {cascadeDeleteData.preview.breakdown.members?.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#262626' }}>
                        會員 ({cascadeDeleteData.preview.breakdown.members.length})
                      </div>
                      <div style={{ fontSize: 11 }}>
                        {cascadeDeleteData.preview.breakdown.members.map((member, idx) => (
                          <div key={idx} style={{ padding: '2px 4px', backgroundColor: '#fff7e6', marginBottom: 2 }}>
                            {member.displayName || member.userId}
                            <span style={{ float: 'right', color: member.balance > 0 ? '#fa8c16' : '#999' }}>
                              ${member.balance?.toLocaleString()}
                            </span>
                          </div>
                        ))}
                        <div style={{ padding: '2px 4px', backgroundColor: '#ffe7ba', fontWeight: 'bold' }}>
                          合計
                          <span style={{ float: 'right' }}>
                            ${cascadeDeleteData.preview.statistics.totalMemberBalance?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            </div>

            {/* 輸入表單 */}
            <Form form={cascadeDeleteForm} layout="vertical">
              {/* 輸入「確認刪除」確認 */}
              <Form.Item
                name="deleteConfirm"
                label={<span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>此操作不可恢復！請輸入「確認刪除」：</span>}
                rules={[
                  { required: true, message: '請輸入確認刪除' },
                  { pattern: /^確認刪除$/, message: '必須輸入「確認刪除」' }
                ]}
              >
                <Input placeholder="輸入「確認刪除」" />
              </Form.Item>

              {/* 輸入密碼 */}
              <Form.Item
                name="password"
                label={<span style={{ fontWeight: 'bold' }}>請輸入你的 SuperAdmin 密碼：</span>}
                rules={[
                  { required: true, message: '請輸入密碼' }
                ]}
              >
                <Input.Password placeholder="輸入密碼" />
              </Form.Item>

              {/* 按鈕組 */}
              <Form.Item style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button
                    onClick={() => {
                      setCascadeDeleteVisible(false);
                      setCascadeDeleteData(null);
                      cascadeDeleteForm.resetFields();
                    }}
                    disabled={cascadeDeleteLoading}
                  >
                    取消
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={async () => {
                      try {
                        const values = await cascadeDeleteForm.validateFields();
                        setCascadeDeleteLoading(true);

                        // 執行級聯刪除
                        const result = await agentService.cascadeDeleteAgent(
                          cascadeDeleteData.agent.id,
                          {
                            password: values.password,
                            reason: ''
                          }
                        );

                        // 顯示成功結果
                        Modal.success({
                          title: '✅ 級聯刪除成功',
                          width: 600,
                          content: (
                            <div>
                              <div style={{ marginBottom: 16 }}>
                                <strong>刪除統計：</strong>
                                <ul style={{ margin: '8px 0 0 0' }}>
                                  <li>刪除代理：{result.statistics.deletedAgents} 個</li>
                                  <li>刪除會員：{result.statistics.deletedMembers} 個</li>
                                  <li>刪除觀察者：{result.statistics.deletedObservers} 個</li>
                                  <li>回收額度：${result.statistics.totalCreditRecovered?.toLocaleString()}</li>
                                  {result.statistics.totalMemberBalanceCleared > 0 && (
                                    <li style={{ color: '#d46b08' }}>
                                      清空會員餘額：${result.statistics.totalMemberBalanceCleared?.toLocaleString()}
                                    </li>
                                  )}
                                  <li>操作耗時：{result.statistics.duration}</li>
                                </ul>
                              </div>
                              <div style={{ fontSize: 12, color: '#888' }}>
                                已記錄操作日誌，可在審計記錄中查看
                              </div>
                            </div>
                          )
                        });

                        // 關閉對話框並刷新列表
                        setCascadeDeleteVisible(false);
                        setCascadeDeleteData(null);
                        cascadeDeleteForm.resetFields();
                        loadAgents();
                        loadDashboard();

                      } catch (error) {
                        if (error.errorFields) {
                          // 表單驗證錯誤，不處理
                          return;
                        }

                        console.error('❌ 級聯刪除執行錯誤', error);
                        console.error('錯誤詳情:', {
                          status: error.response?.status,
                          data: error.response?.data,
                          message: error.message
                        });

                        // 檢查是否為密碼錯誤
                        if (error.response?.data?.error === 'INVALID_PASSWORD' || error.response?.status === 401) {
                          window.alert('❌ 密碼錯誤\n\n' + (error.response?.data?.message || 'SuperAdmin 密碼不正確，請重新輸入'));
                        } else if (error.response?.status === 400) {
                          // 400 錯誤（如帳目不符）
                          window.alert('❌ 刪除失敗\n\n' + (error.response?.data?.error || error.message || '操作失敗'));
                        } else if (error.response?.status === 500) {
                          // 500 內部錯誤
                          window.alert('❌ 系統錯誤\n\n伺服器內部錯誤，請查看後端日誌或聯繫技術支援');
                        } else {
                          // 其他錯誤
                          window.alert('❌ 刪除失敗\n\n' + (error.response?.data?.error || error.message || '未知錯誤'));
                        }
                      } finally {
                        setCascadeDeleteLoading(false);
                      }
                    }}
                    loading={cascadeDeleteLoading}
                  >
                    確認刪除
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Agents;